import { z } from 'zod';
/**
 * API keys and signed webhooks for insurer systems (task 71, decision 0047). An API key reads the book
 * through the versioned `/v1` API, limited to its scopes and to what an auditor may see; it can't change
 * anything and can't use the app's own routes. Webhooks send chosen events to the insurer's system, signed
 * so it can tell they came from CyberScore, and retried until they are taken.
 */
export const API_SCOPES = ['clients:read', 'scores:read', 'decisions:read', 'alerts:read'];
export const API_SCOPE_LABEL = {
    'clients:read': 'Clients and their policy fields',
    'scores:read': 'CyberScores',
    'decisions:read': 'Underwriting decisions and conditions',
    'alerts:read': 'Alerts',
};
/** Keys look like `csk_<8-character id>_<32-character secret>`; only a hash of the secret is kept. */
export const API_KEY_PATTERN = /^csk_([A-Za-z0-9]{8})_([A-Za-z0-9_-]{32,64})$/;
export const CreateApiKey = z.strictObject({
    name: z.string().trim().min(1).max(80),
    scopes: z.array(z.enum(API_SCOPES)).min(1).max(API_SCOPES.length),
    /** Days until it stops working; none keeps it until revoked. */
    expires_in_days: z.number().int().min(1).max(730).nullable().default(365),
});
// —— webhooks ——
export const WEBHOOK_EVENTS = [
    'alert.raised',
    'assessment.submitted',
    'assessment.decided',
    'assessment.score_changed',
    'condition.evidenced',
    'condition.cleared',
    'client.created',
    'client.updated',
    'intake.submitted',
    'campaign.check_started',
];
export const WEBHOOK_EVENT_LABEL = {
    'alert.raised': 'An alert rule raised an alert',
    'assessment.submitted': 'A business submitted its check',
    'assessment.decided': 'An underwriting decision was made',
    'assessment.score_changed': 'A CyberScore changed',
    'condition.evidenced': 'A business sent evidence for a condition',
    'condition.cleared': 'A condition was met',
    'client.created': 'A client record was added',
    'client.updated': 'A client record changed',
    'intake.submitted': 'A business sent its details through a submission link',
    'campaign.check_started': 'A renewal campaign started a check',
};
/** Retries after a failed delivery, in minutes; then it stops and shows as failed. */
export const WEBHOOK_RETRY_MINUTES = [1, 5, 30, 120, 720, 1440];
export const WEBHOOK_TIMEOUT_MS = 10_000;
/** http(s), a host (no user name or password) with an optional port, a path and query, no #. */
const WEBHOOK_URL = /^https?:\/\/(\[[0-9a-fA-F:.]+\]|[A-Za-z0-9.-]+)(:\d{1,5})?(\/[^\s#]*)?$/;
const url = z
    .string()
    .trim()
    .max(500)
    .refine((u) => WEBHOOK_URL.test(u), 'An http(s) address without a user name, password or #');
const endpoint = {
    url,
    description: z.string().trim().max(200).default(''),
    events: z.array(z.enum(WEBHOOK_EVENTS)).min(1).max(WEBHOOK_EVENTS.length),
    active: z.boolean().default(true),
};
export const CreateWebhook = z.strictObject(endpoint);
export const UpdateWebhook = z.strictObject({ revision: z.number().int().min(1), ...endpoint });
/** The signature header: `t=<unix seconds>,v1=<hex HMAC-SHA256 of "<t>.<body>" with the secret>`. */
export const SIGNATURE_HEADER = 'CyberScore-Signature';
export const signedText = (timestamp, body) => `${timestamp}.${body}`;
export const signatureHeader = (timestamp, hexMac) => `t=${timestamp},v1=${hexMac}`;
export function parseSignature(header) {
    const parts = header.split(',').map((p) => p.trim().split('='));
    const t = Number(parts.find(([k]) => k === 't')?.[1]);
    const v1 = parts.filter(([k, v]) => k === 'v1' && v).map(([, v]) => v);
    return Number.isInteger(t) && v1.length ? { t, v1 } : null;
}
/** When a failed delivery is tried again, or null when it has run out of tries. */
export function nextAttempt(attempts, now) {
    const m = WEBHOOK_RETRY_MINUTES[attempts - 1];
    return m === undefined ? null : new Date(now.getTime() + m * 60_000);
}
//# sourceMappingURL=integrations.js.map